// ============================================================
// Positioning strategy router — TWS-122 Wave 3
// ------------------------------------------------------------
// Decides whether a primitive takes the native `[popover]` +
// CSS anchor-positioning fast path, or falls back to the CDK
// Overlay path. Every Wave 1/2 primitive consults this helper
// once in its constructor and branches its open/close logic.
//
// The two underlying detects (`supportsAnchorPositioning` +
// `supportsPopoverApi`) each memoise their result — this helper
// memoises the composition so the per-open code path stays
// branch-free after the first call.
//
// Modern browsers (Chrome/Edge 125+, Safari 26.1+, Firefox 149+)
// take the native path with zero added JS cost. Older browsers
// (anything lacking either anchor positioning OR the base
// `[popover]` API) take the CDK fallback.
// ============================================================

import {
  supportsAnchorPositioning,
  supportsPopoverApi,
} from './anchor-positioning';

/**
 * Which runtime positioning path the primitives should use.
 *
 * - `'native'` — `[popover]` + CSS anchor positioning (+ optional
 *   `popover="hint"` for tooltips). No JS positioning cost.
 * - `'cdk-fallback'` — CDK Overlay's `FlexibleConnectedPositionStrategy`
 *   handles placement; a JS light-dismiss shim supplies the
 *   outside-click + Escape behaviour that native `[popover]`
 *   gives for free.
 */
export type TwPopoverPositioningStrategy = 'native' | 'cdk-fallback';

let memoised: TwPopoverPositioningStrategy | null = null;

/**
 * Resolve the positioning strategy for this process.
 *
 * Memoised — the underlying browser capability cannot change at
 * runtime, and per-open branching should not repeatedly probe
 * the CSSOM. Tests that want to simulate the fallback path can
 * call `__resetPositioningStrategyForTesting()` after stubbing
 * `CSS.supports` / the popover prototype methods, then query
 * this helper again.
 */
export function getPositioningStrategy(): TwPopoverPositioningStrategy {
  if (memoised !== null) return memoised;
  memoised =
    supportsAnchorPositioning() && supportsPopoverApi()
      ? 'native'
      : 'cdk-fallback';
  return memoised;
}

/**
 * Convenience: true when the native path is available. Slightly
 * more readable at call sites that only need the boolean.
 */
export function isNativePositioningAvailable(): boolean {
  return getPositioningStrategy() === 'native';
}

/**
 * Test-only escape hatch. NOT exported from the public barrel.
 *
 * @internal
 */
export function __resetPositioningStrategyForTesting(): void {
  memoised = null;
}
